import { useLocalization } from "@fluent/react";
import { Button, Row } from "@narmi/design_system";
import { useEffect, useState } from "react";
import { BackButton } from "@lending/components/BackButton/BackButton";
import { Card } from "@lending/components/Card/Card";
import { EditInfoDrawer } from "@lending/components/EditInfoDrawer/EditInfoDrawer";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import { InfoRow } from "@lending/components/InfoRow/InfoRow";
import { PageHeader } from "@lending/components/PageHeader";
import { PageLayout } from "@lending/components/PageLayout/PageLayout";
import { PlainButton } from "@lending/components/PlainButton/PlainButton";
import { useGetApplicationApplicantDetailsSensitiveQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useGetApplicationApplicantDetailsSensitiveQuery";
import { useListApplicationApplicantDetailsQuery } from "@lending/hooks/queries/ApplicationApplicantDetails/useListApplicationApplicantDetailsQuery";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { useGetVehicleLoanApplicationQuery } from "@lending/hooks/queries/VehicleLoanApplication/useGetVehicleLoanApplicationQuery";
import { useCurrentApplicationUuid } from "@lending/hooks/useCurrentApplicationUuid";
import { useToastsContext } from "@lending/hooks/useToasts";
import { useFlowNavigation } from "@lending/navigation";
import { InfoAlert } from "@lending/pages/Common/components/InfoAlert";
import { findPrimaryApplicant, isMissingContactDetails } from "@lending/utils/applicantHelpers";
import { formatAddress, formatDate, formatPhoneNumber } from "@lending/utils/formatters";
import styles from "./ConfirmInfo.module.css";
import { useStore } from "../../../contexts/StoreContext";

function ConfirmInfo() {
  const { l10n } = useLocalization();
  const { institution } = useGetInstitutionQuery();
  const { goNext, goToPath } = useFlowNavigation();
  const { showErrorToast } = useToastsContext();
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const uuid = useCurrentApplicationUuid();
  const { updateData } = useStore();
  const {
    data: application,
    isLoading,
    isError,
  } = useGetVehicleLoanApplicationQuery({ applicationUuid: uuid });
  const { data: applicantDetailsData } = useListApplicationApplicantDetailsQuery({
    applicationUuid: uuid,
  });
  const applicant = findPrimaryApplicant(applicantDetailsData?.results);
  const { data: sensitiveApplicantData } = useGetApplicationApplicantDetailsSensitiveQuery(
    applicant?.id ?? ""
  );

  const residentialAddress = applicant?.residential_address;
  const mailingAddress = applicant?.mailing_address;

  // required for subsequent requests to update the application
  useEffect(() => {
    updateData({ lendingApplicationID: uuid });
  }, [updateData, uuid]);

  useEffect(() => {
    function checkOffers() {
      if (!application) return;
      switch (application.state) {
        case "creating":
          return;
        case "offers_generated":
          return goToPath(`/olb-auto-loan/${uuid}/approved`, { replace: true });
        case "offer_accepted":
          return goToPath(`/olb-auto-loan/${uuid}/submitted`, { replace: true });
        default:
          return goToPath("/product-selection", { replace: true });
      }
    }
    void checkOffers();
  }, [application, goToPath, uuid]);

  function onClickNext() {
    if (applicant && isMissingContactDetails(applicant)) {
      showErrorToast(
        l10n.getString(
          "confirm-info-missing-details",
          null,
          "Please fill in the missing details to continue."
        )
      );
      setIsDrawerOpen(true);
      return;
    }
    goNext();
  }

  if (isError) {
    return (
      <PageLayout style={{ marginTop: "var(--space-xxl)" }}>
        <ErrorScreen />
      </PageLayout>
    );
  }

  return (
    <>
      <PageLayout
        isLoading={isLoading || !application || !applicant}
        style={{ marginTop: "var(--space-xxl)" }}
      >
        <div className={styles.container}>
          <div className={styles.content}>
            <PageHeader
              heading={l10n.getString(
                "confirm-info-heading",
                null,
                "First, let's confirm your details"
              )}
              subheading={l10n.getString(
                "confirm-info-subheading",
                null,
                "This helps us verify your identity and create your custom offer."
              )}
            />
            <div className="margin--bottom--l">
              {applicant && (
                <Card key={applicant.id}>
                  <div className={styles.applicantInfo}>
                    <div className={styles.edit}>
                      <PlainButton
                        label={l10n.getString("confirm-edit-button", null, "Edit")}
                        onClick={() => setIsDrawerOpen(true)}
                      />
                    </div>
                    <InfoRow
                      label={l10n.getString("confirm-name-label", null, "Name")}
                      value={`${applicant.first_name} ${applicant.last_name}`}
                    />
                    <InfoRow
                      label={l10n.getString("confirm-ssn-label", null, "SSN")}
                      value={sensitiveApplicantData?.tax_id}
                    />
                    <InfoRow
                      label={l10n.getString("confirm-dob-label", null, "Date of birth")}
                      value={
                        sensitiveApplicantData?.date_of_birth
                          ? formatDate(sensitiveApplicantData?.date_of_birth ?? "")
                          : ""
                      }
                    />
                    <InfoRow
                      label={l10n.getString("confirm-email-label", null, "Email")}
                      value={applicant.email}
                    />
                    <InfoRow
                      label={l10n.getString("confirm-phone-label", null, "Phone")}
                      value={formatPhoneNumber(applicant.phone)}
                    />
                    <InfoRow
                      label={l10n.getString(
                        "confirm-residential-address-label",
                        null,
                        "Residential address"
                      )}
                      value={residentialAddress ? formatAddress(residentialAddress) : ""}
                    />
                    {institution.lending_ask_mailing_address && (
                      <InfoRow
                        label={l10n.getString(
                          "confirm-mailing-address-label",
                          null,
                          "Mailing address"
                        )}
                        value={
                          mailingAddress
                            ? formatAddress(mailingAddress)
                            : l10n.getString(
                                "confirm-mailing-address-same-as-residential",
                                null,
                                "Same as residential address"
                              )
                        }
                      />
                    )}
                  </div>
                </Card>
              )}
            </div>
            <div className="margin--bottom--xxl">
              <InfoAlert
                message={l10n.getString(
                  "confirm-info-alert",
                  null,
                  "Checking your rate will not affect your credit score."
                )}
              />
            </div>
            <Row alignItems="center" justifyContent="space-between">
              {/* confirm-info is the OLB flow's entry step, so there is no in-flow previous
                  step; Back returns to the Loan Portal it was launched from rather than
                  falling through goBack -> startOver, which lands on web product-selection. */}
              <BackButton onClick={() => goToPath("/portal")} />
              <Button
                label={l10n.getString("confirm-submit-button", null, "Looks good!")}
                onClick={onClickNext}
              />
            </Row>
          </div>
        </div>
      </PageLayout>
      {/* TODO Find a better way to populate lendingApplicant */}
      {application && applicant && sensitiveApplicantData && (
        <EditInfoDrawer
          applicationUuid={application.id}
          lendingApplicant={applicant}
          identity={sensitiveApplicantData}
          isDrawerOpen={isDrawerOpen}
          setIsDrawerOpen={setIsDrawerOpen}
        />
      )}
    </>
  );
}

export { ConfirmInfo };
